/**
 * Shared timeline primitives used by:
 *   - src/pages/orders/TimelineEstimator.tsx  (order create/edit form)
 *   - src/pages/videoProduction/components/ProjectDetailsSidebar.tsx  (project detail page)
 */
import React from "react";
import { Box, Step, StepConnector, StepLabel, Stepper, Typography } from "@mui/material";
import { stepConnectorClasses } from "@mui/material/StepConnector";
import { styled } from "@mui/material/styles";
import { IconPage, IconBook, IconCheck, IconUser, IconExpert, IconClock, IconCalendar, IconCamera, IconVideo, IconShare } from "../../utils/icons";

// ─── Constants ────────────────────────────────────────────────────────────────

export const PRIMARY_COLOR = "var(--accent)";

export const STATIC_DAYS = {
    briefApproval: 5,
    expertSelection: 5,
    filmDateScheduling: 5,
    filming: 1,
    postProductionBase: 7,
    postProductionPerProject: 1,
    reviewPerProject: 1,
};

// ─── Milestone calculation ────────────────────────────────────────────────────

export function getMilestones(numProjects: number) {
    const briefApproval = STATIC_DAYS.briefApproval;
    const expertSelection = briefApproval + STATIC_DAYS.expertSelection;
    const filmDateScheduling = expertSelection + STATIC_DAYS.filmDateScheduling;
    const filming = filmDateScheduling + STATIC_DAYS.filming;
    const postProduction =
        filming +
        STATIC_DAYS.postProductionBase +
        (STATIC_DAYS.postProductionPerProject + STATIC_DAYS.reviewPerProject) * numProjects;
    const delivery = postProduction + 1;

    return [
        { label: "Brief Approval", daysOffset: briefApproval },
        { label: "Expert Selection", daysOffset: expertSelection },
        { label: "Film Date Scheduling", daysOffset: filmDateScheduling },
        { label: "Filming", daysOffset: filming },
        { label: "Post-Production", daysOffset: postProduction },
        { label: "Earliest Possible Delivery", daysOffset: delivery },
    ];
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

export function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

export function formatDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
}

// ─── Styled connector ─────────────────────────────────────────────────────────

export const TimelineConnector = styled(StepConnector)({
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: "var(--timeline-upcoming, #D4C5B0)",
        borderLeftWidth: 2,
        minHeight: 16,
    },
});

// ─── Step icons ───────────────────────────────────────────────────────────────

export const SchedulingIcon: React.FC = () => <IconClock size={20} stroke="white" />;

export const STEP_ICONS: Array<React.FC> = [
    () => <IconPage size={20} stroke="white" />,          // Video Requested
    () => <IconBook width={20} height={20} stroke="white" />,    // Brief Submitted
    () => <IconCheck width={20} height={20} stroke="white" />,   // Brief Approval
    () => <IconUser width={20} height={20} stroke="white" />,     // Expert Selection
    () => <IconExpert width={20} height={20} stroke="white" />, // Expert Selected
    () => <IconCalendar size={20} fill="white" />,         // Pre-Production
    () => <IconCamera size={20} stroke="white" />,         // Filming
    () => <IconVideo size={20} stroke="white" />,          // Editing
    () => <IconShare size={20} stroke="white" />,          // Delivered
];

// ─── Step label styles ────────────────────────────────────────────────────────

export const stepLabelSx = {
    alignItems: "flex-center",
    "& .MuiStepLabel-iconContainer": {
        background: PRIMARY_COLOR,
        p: 1,
        mr: 1,
        borderRadius: "100%",
        aspectRatio: 1,
        transform: "translateX(-15%)",
    },
};

// ─── MilestoneStepper ─────────────────────────────────────────────────────────

export interface MilestoneItem {
    label: string;
    /** Formatted text shown below the label (e.g. "04/28/2026", "N/A", "Est. 04/28/2026") */
    text: string;
    textColor?: string;
    fontStyle?: string;
    state?: "actual" | "unavailable" | "estimated" | "committed";
    icon?: React.FC;
}

export function MilestoneStepper({ milestones }: { milestones: MilestoneItem[] }) {
    return (
        <Stepper orientation="vertical" connector={<TimelineConnector />} sx={{ p: 0, ml: 1 }}>
            {milestones.map((milestone, index) => (
                <Step key={milestone.label} active>
                    <StepLabel StepIconComponent={milestone.icon ?? STEP_ICONS[index]} sx={stepLabelSx}>
                        <Box sx={{ lineHeight: 1.2 }}>
                            <Typography
                                sx={{
                                    fontFamily: "CircularStd, sans-serif",
                                    fontWeight: 600,
                                    fontSize: 14,
                                    color: "var(--text)",
                                    lineHeight: 1.4,
                                }}
                            >
                                {milestone.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "CircularStd, sans-serif",
                                    fontSize: 13,
                                    color: milestone.textColor ?? "var(--text-2)",
                                    lineHeight: 1.4,
                                    fontStyle: milestone.fontStyle ?? "normal",
                                }}
                            >
                                {milestone.text}
                            </Typography>
                        </Box>
                    </StepLabel>
                </Step>
            ))}
        </Stepper>
    );
}

// ─── HorizontalMilestoneStepper ───────────────────────────────────────────────

export function HorizontalMilestoneStepper({ milestones }: { milestones: MilestoneItem[] }) {
    return (
        <div style={{ overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none" } as React.CSSProperties}>
            <div style={{ display: "flex", alignItems: "flex-start", minWidth: "max-content", padding: "4px 8px" }}>
                {milestones.map((milestone, index) => {
                    const Icon = milestone.icon ?? STEP_ICONS[index];
                    const isDone = milestone.state === "actual" || milestone.state === "unavailable";
                    const iconBg = isDone ? PRIMARY_COLOR : "var(--timeline-upcoming, #D4C5B0)";
                    const connectorBg = isDone
                        ? PRIMARY_COLOR
                        : "repeating-linear-gradient(90deg, var(--timeline-upcoming, #D4C5B0) 0px, var(--timeline-upcoming, #D4C5B0) 6px, transparent 6px, transparent 12px)";
                    return (
                        <React.Fragment key={milestone.label}>
                            {index > 0 && (
                                <div
                                    style={{
                                        flex: "1 1 24px",
                                        minWidth: 24,
                                        height: 2,
                                        marginTop: 21,
                                        alignSelf: "flex-start",
                                        background: connectorBg,
                                    }}
                                />
                            )}
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 88 }}>
                                <div
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: "50%",
                                        background: iconBg,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Icon />
                                </div>
                                <Typography
                                    sx={{
                                        fontFamily: "CircularStd, sans-serif",
                                        fontWeight: 600,
                                        fontSize: 12,
                                        color: "var(--text)",
                                        textAlign: "center",
                                        mt: 1,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {milestone.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "CircularStd, sans-serif",
                                        fontSize: 11,
                                        color: milestone.textColor ?? "var(--text-2)",
                                        textAlign: "center",
                                        lineHeight: 1.3,
                                        fontStyle: milestone.fontStyle ?? "normal",
                                    }}
                                >
                                    {milestone.text}
                                </Typography>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}
